/**
 * sankhya v7.2 — Safe API Client with Zod Validation
 *
 * Wraps axios calls with runtime Zod schema validation.
 * On validation failure: logs a warning but returns the raw data (graceful degradation).
 *
 * Usage:
 *   import { safeApiCall, api } from "@/lib/api";
 *   import { StatsResponseSchema } from "@/lib/schemas";
 *   const stats = await safeApiCall(() => api.get("/stats"), StatsResponseSchema);
 */
import axios from "axios";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

// Create axios instance with defaults
export const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Set API key header if configured
if (process.env.REACT_APP_API_KEY) {
  api.defaults.headers.common["X-API-Key"] = process.env.REACT_APP_API_KEY;
}

// Support for JWT auth tokens in production
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("sankhya_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

/**
 * Safe API call with optional Zod validation.
 *
 * @param {Function} apiCall - Function that returns an axios promise
 * @param {import("zod").ZodSchema} [schema] - Optional Zod schema to validate response
 * @returns {Promise<any>} - Parsed (or raw if validation fails) response data
 */
export async function safeApiCall(apiCall, schema) {
  const response = await apiCall();
  const data = response.data;

  if (!schema) return data;

  const result = schema.safeParse(data);
  if (!result.success) {
    if (process.env.NODE_ENV === "development") {
      console.warn(
        "[Zod] API response validation failed:",
        result.error.issues.map((i) => `${i.path.join(".")}: ${i.message}`).join(", ")
      );
    }
    // Graceful degradation: return raw data, don't crash
    return data;
  }
  return result.data;
}

/**
 * Convenience: GET with validation.
 */
export async function safeGet(url, schema, config) {
  return safeApiCall(() => api.get(url, config), schema);
}

/**
 * Convenience: POST with validation.
 */
export async function safePost(url, body, schema, config) {
  return safeApiCall(() => api.post(url, body, config), schema);
}

export default api;
